import React, { useState, useEffect, useCallback } from 'react';
import {
    Save,
    X,
    Plus,
    Play,
    Clock,
    Mail,
    AlertCircle,
    CheckCircle,
    Loader2,
    BarChart3
} from 'lucide-react';
import {
    claudeQueryService,
    DigestDTO,
    DigestSectionDTO,
    SavedReportDTO,
    AvailableMetricDTO
} from '../../../../../services/claudeQueryService';
import { DigestSectionEditor } from './DigestSectionEditor';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface DigestEditorProps {
    digest: DigestDTO | null; // null = nytt digest
    onSave: () => void;
    onCancel: () => void;
}

export const DigestEditor: React.FC<DigestEditorProps> = ({
    digest,
    onSave,
    onCancel
}) => {
    const { t } = useTranslation();
    // Felter
    const [name, setName] = useState(digest?.name || '');
    const [description, setDescription] = useState(digest?.description || '');
    const [deliveryEmail, setDeliveryEmail] = useState(digest?.deliveryEmail || '');
    const [headerTitle, setHeaderTitle] = useState(digest?.headerTitle || t('manager.modules.claudeQuery.digests.defaultHeaderTitle', {}, 'Daglig Sammendrag'));
    const [headerSubtitle, setHeaderSubtitle] = useState(digest?.headerSubtitle || '');
    const [sections, setSections] = useState<DigestSectionDTO[]>(digest?.sections || []);

    // Scheduling
    const [scheduleEnabled, setScheduleEnabled] = useState(digest?.scheduleEnabled || false);
    const [scheduleFrequency, setScheduleFrequency] = useState('daily');
    const [scheduleHour, setScheduleHour] = useState(6);
    const [scheduleMinute, setScheduleMinute] = useState(0);
    const [scheduleDayOfWeek, setScheduleDayOfWeek] = useState(1);
    const [scheduleDayOfMonth, setScheduleDayOfMonth] = useState(1);

    // Reference data
    const [reports, setReports] = useState<SavedReportDTO[]>([]);
    const [metrics, setMetrics] = useState<AvailableMetricDTO[]>([]);

    // UI state
    const [saving, setSaving] = useState(false);
    const [executing, setExecuting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    // Parse existing cron
    useEffect(() => {
        if (digest?.scheduleCron) {
            const parts = digest.scheduleCron.split(' ');
            if (parts.length >= 5) {
                setScheduleMinute(parseInt(parts[0]) || 0);
                setScheduleHour(parseInt(parts[1]) || 6);
                if (parts[2] !== '*') {
                    setScheduleFrequency('monthly');
                    setScheduleDayOfMonth(parseInt(parts[2]) || 1);
                } else if (parts[4] !== '*') {
                    setScheduleFrequency('weekly');
                    setScheduleDayOfWeek(parseInt(parts[4]) || 1);
                } else {
                    setScheduleFrequency('daily');
                }
            }
        }
    }, [digest]);

    // Load reference data
    const loadReferenceData = useCallback(async () => {
        try {
            const [reportsData, metricsData] = await Promise.all([
                claudeQueryService.getReports(),
                claudeQueryService.getAvailableMetrics()
            ]);
            setReports(reportsData);
            setMetrics(metricsData);
        } catch (err: any) {
            console.error('Failed to load reference data:', err);
        }
    }, []);

    useEffect(() => {
        loadReferenceData();
    }, [loadReferenceData]);

    const buildCronExpression = (): string => {
        switch (scheduleFrequency) {
            case 'daily':
                return `${scheduleMinute} ${scheduleHour} * * *`;
            case 'weekly':
                return `${scheduleMinute} ${scheduleHour} * * ${scheduleDayOfWeek}`;
            case 'monthly':
                return `${scheduleMinute} ${scheduleHour} ${scheduleDayOfMonth} * *`;
            default:
                return `${scheduleMinute} ${scheduleHour} * * *`;
        }
    };

    const getScheduleDescription = (): string => {
        const time = `${String(scheduleHour).padStart(2, '0')}:${String(scheduleMinute).padStart(2, '0')}`;
        const days = [
            t('manager.modules.claudeQuery.common.sunday', {}, 'Søndag'),
            t('manager.modules.claudeQuery.common.monday', {}, 'Mandag'),
            t('manager.modules.claudeQuery.common.tuesday', {}, 'Tirsdag'),
            t('manager.modules.claudeQuery.common.wednesday', {}, 'Onsdag'),
            t('manager.modules.claudeQuery.common.thursday', {}, 'Torsdag'),
            t('manager.modules.claudeQuery.common.friday', {}, 'Fredag'),
            t('manager.modules.claudeQuery.common.saturday', {}, 'Lørdag'),
        ];
        switch (scheduleFrequency) {
            case 'daily':
                return t('manager.modules.claudeQuery.digests.scheduleDaily', { '0': time }, `Hver dag kl. ${time}`);
            case 'weekly':
                return t('manager.modules.claudeQuery.digests.scheduleWeekly', { '0': days[scheduleDayOfWeek], '1': time }, `Hver ${days[scheduleDayOfWeek]} kl. ${time}`);
            case 'monthly':
                return t('manager.modules.claudeQuery.digests.scheduleMonthly', { '0': scheduleDayOfMonth, '1': time }, `Den ${scheduleDayOfMonth}. hver måned kl. ${time}`);
            default:
                return '';
        }
    };

    const handleAddSection = () => {
        setSections(prev => [
            ...prev,
            {
                sectionType: 'dashboard-metric',
                sourceId: '',
                title: '',
                sortOrder: prev.length + 1
            }
        ]);
    };

    const handleSectionChange = (index: number, section: DigestSectionDTO) => {
        setSections(prev => prev.map((s, i) => i === index ? { ...section, sortOrder: i + 1 } : s));
    };

    const handleSectionRemove = (index: number) => {
        setSections(prev => prev.filter((_, i) => i !== index).map((s, i) => ({ ...s, sortOrder: i + 1 })));
    };

    const handleSectionMoveUp = (index: number) => {
        if (index === 0) return;
        setSections(prev => {
            const next = [...prev];
            [next[index - 1], next[index]] = [next[index], next[index - 1]];
            return next.map((s, i) => ({ ...s, sortOrder: i + 1 }));
        });
    };

    const handleSectionMoveDown = (index: number) => {
        if (index >= sections.length - 1) return;
        setSections(prev => {
            const next = [...prev];
            [next[index], next[index + 1]] = [next[index + 1], next[index]];
            return next.map((s, i) => ({ ...s, sortOrder: i + 1 }));
        });
    };

    const handleSave = async () => {
        try {
            setError(null);
            setSaving(true);

            if (!name.trim()) { setError(t('manager.modules.claudeQuery.digests.errorNameRequired', {}, 'Navn er påkrevd')); return; }
            if (!deliveryEmail.trim()) { setError(t('manager.modules.claudeQuery.digests.errorEmailRequired', {}, 'E-postadresse er påkrevd')); return; }
            if (sections.length === 0) { setError(t('manager.modules.claudeQuery.digests.errorMinOneSection', {}, 'Legg til minst én seksjon')); return; }

            const invalidSection = sections.find(s => !s.sourceId);
            if (invalidSection) { setError(t('manager.modules.claudeQuery.digests.errorSectionSource', {}, 'Alle seksjoner må ha en kilde valgt')); return; }

            const dto = {
                name: name.trim(),
                description: description.trim() || undefined,
                sections,
                scheduleEnabled,
                scheduleCron: scheduleEnabled ? buildCronExpression() : undefined,
                deliveryEmail: deliveryEmail.trim(),
                headerTitle: headerTitle.trim() || t('manager.modules.claudeQuery.digests.defaultHeaderTitle', {}, 'Daglig Sammendrag'),
                headerSubtitle: headerSubtitle.trim() || undefined
            };

            if (digest) {
                await claudeQueryService.updateDigest(digest.digestId, dto);
            } else {
                await claudeQueryService.createDigest(dto);
            }

            onSave();
        } catch (err: any) {
            setError(err.message || t('manager.modules.claudeQuery.digests.saveError', {}, 'Kunne ikke lagre digest'));
        } finally {
            setSaving(false);
        }
    };

    const handleExecute = async () => {
        if (!digest) return;

        try {
            setExecuting(true);
            setError(null);
            setSuccessMessage(null);

            const result = await claudeQueryService.executeDigest(digest.digestId);

            if (result.success) {
                setSuccessMessage(t('manager.modules.claudeQuery.digests.sentTo', { '0': deliveryEmail }, `Sammendrag sendt til ${deliveryEmail}`));
            } else {
                setError(result.message || t('manager.modules.claudeQuery.digests.sendFailed', {}, 'Sending feilet'));
            }
        } catch (err: any) {
            setError(err.message || t('manager.modules.claudeQuery.digests.executeError', {}, 'Kunne ikke kjøre digest'));
        } finally {
            setExecuting(false);
        }
    };

    return (
        <div className="h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">
                    {digest ? t('manager.modules.claudeQuery.digests.editTitle', { '0': digest.name }, `Rediger: ${digest.name}`) : t('manager.modules.claudeQuery.digests.newDigest', {}, 'Nytt sammendrag')}
                </h2>
                <div className="flex items-center gap-2">
                    {digest && (
                        <button
                            onClick={handleExecute}
                            disabled={executing}
                            className="flex items-center gap-1.5 px-3 py-2 bg-green-500 text-white rounded-lg text-sm
                                     hover:bg-green-600 disabled:opacity-50 font-medium transition-colors"
                        >
                            {executing ? (
                                <Loader2 size={14} className="animate-spin" />
                            ) : (
                                <Play size={14} />
                            )}
                            {executing ? t('manager.modules.claudeQuery.digests.sending', {}, 'Sender...') : t('manager.modules.claudeQuery.digests.runNow', {}, 'Kjør nå')}
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="flex items-center gap-1.5 px-4 py-2 bg-blue-500 text-white rounded-lg text-sm
                                 hover:bg-blue-600 disabled:opacity-50 font-medium transition-colors"
                    >
                        {saving ? (
                            <Loader2 size={14} className="animate-spin" />
                        ) : (
                            <Save size={14} />
                        )}
                        {saving ? t('common.status.saving', {}, 'Lagrer...') : t('common.buttons.save', {}, 'Lagre')}
                    </button>
                    <button
                        onClick={onCancel}
                        className="flex items-center gap-1.5 px-3 py-2 text-gray-600 hover:bg-gray-100
                                 rounded-lg text-sm transition-colors"
                    >
                        <X size={14} />
                        {t('common.buttons.cancel', {}, 'Avbryt')}
                    </button>
                </div>
            </div>

            {/* Messages */}
            {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-4 text-sm text-red-700">
                    <AlertCircle size={16} />
                    {error}
                    <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
                </div>
            )}
            {successMessage && (
                <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4 text-sm text-green-700">
                    <CheckCircle size={16} />
                    {successMessage}
                    <button onClick={() => setSuccessMessage(null)} className="ml-auto text-green-400 hover:text-green-600">&times;</button>
                </div>
            )}

            {/* Form */}
            <div className="flex-1 overflow-auto space-y-6">
                {/* Grunnleggende info */}
                <section>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('manager.modules.claudeQuery.digests.sectionBasic', {}, 'Grunnleggende')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">{t('manager.modules.claudeQuery.digests.fieldName', {}, 'Navn *')}</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t('manager.modules.claudeQuery.digests.namePlaceholder', {}, 'F.eks. Morgenrapport Mandag')}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                                         focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">
                                <Mail size={12} className="inline mr-1" />
                                {t('manager.modules.claudeQuery.digests.fieldDeliveryEmail', {}, 'Leveringse-post *')}
                            </label>
                            <input
                                type="text"
                                value={deliveryEmail}
                                onChange={(e) => setDeliveryEmail(e.target.value)}
                                placeholder={t('manager.modules.claudeQuery.digests.deliveryEmailPlaceholder', {}, 'lager@firma.no, sjef@firma.no')}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                                         focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            />
                            <p className="text-xs text-gray-400 mt-1">{t('manager.modules.claudeQuery.digests.multipleAddresses', {}, 'Flere adresser separert med komma')}</p>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm text-gray-600 mb-1">{t('common.fields.description', {}, 'Beskrivelse')}</label>
                            <textarea
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                placeholder={t('manager.modules.claudeQuery.digests.descriptionPlaceholder', {}, 'Valgfri beskrivelse av sammendraget...')}
                                rows={2}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none
                                         focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            />
                        </div>
                    </div>
                </section>

                {/* E-post-branding */}
                <section>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">{t('manager.modules.claudeQuery.digests.sectionEmailLook', {}, 'E-post utseende')}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">{t('manager.modules.claudeQuery.digests.fieldHeader', {}, 'Overskrift')}</label>
                            <input
                                type="text"
                                value={headerTitle}
                                onChange={(e) => setHeaderTitle(e.target.value)}
                                placeholder={t('manager.modules.claudeQuery.digests.defaultHeaderTitle', {}, 'Daglig Sammendrag')}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                                         focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            />
                        </div>
                        <div>
                            <label className="block text-sm text-gray-600 mb-1">{t('manager.modules.claudeQuery.digests.fieldSubtitle', {}, 'Undertittel')}</label>
                            <input
                                type="text"
                                value={headerSubtitle}
                                onChange={(e) => setHeaderSubtitle(e.target.value)}
                                placeholder={t('manager.modules.claudeQuery.digests.subtitlePlaceholder', {}, 'Ukentlig lagerrapport for Hovedlager')}
                                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                                         focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200"
                            />
                        </div>
                    </div>
                </section>

                {/* Seksjoner */}
                <section>
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="text-sm font-semibold text-gray-700">
                            {t('manager.modules.claudeQuery.digests.sectionsHeading', { '0': sections.length }, `Seksjoner (${sections.length})`)}
                        </h3>
                        <button
                            onClick={handleAddSection}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-blue-600 bg-blue-50
                                     hover:bg-blue-100 rounded-lg text-sm font-medium transition-colors"
                        >
                            <Plus size={14} />
                            {t('manager.modules.claudeQuery.digests.addSection', {}, 'Legg til seksjon')}
                        </button>
                    </div>

                    {sections.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg">
                            <BarChart3 size={32} className="mx-auto mb-2 text-gray-300" />
                            <p className="text-sm">{t('manager.modules.claudeQuery.digests.noSections', {}, 'Ingen seksjoner ennå')}</p>
                            <p className="text-xs mt-1">{t('manager.modules.claudeQuery.digests.noSectionsHint', {}, 'Legg til dashboard-metrikker eller lagrede rapporter')}</p>
                            <button
                                onClick={handleAddSection}
                                className="mt-3 text-sm text-blue-500 hover:text-blue-600 font-medium"
                            >
                                {t('manager.modules.claudeQuery.digests.addFirstSection', {}, '+ Legg til første seksjon')}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {sections.map((section, idx) => (
                                <DigestSectionEditor
                                    key={idx}
                                    section={section}
                                    index={idx}
                                    reports={reports}
                                    metrics={metrics}
                                    onChange={handleSectionChange}
                                    onRemove={handleSectionRemove}
                                    onMoveUp={handleSectionMoveUp}
                                    onMoveDown={handleSectionMoveDown}
                                    isFirst={idx === 0}
                                    isLast={idx === sections.length - 1}
                                />
                            ))}
                        </div>
                    )}
                </section>

                {/* Scheduling */}
                <section>
                    <div className="flex items-center gap-3 mb-3">
                        <h3 className="text-sm font-semibold text-gray-700">
                            <Clock size={14} className="inline mr-1" />
                            {t('manager.modules.claudeQuery.common.scheduling', {}, 'Scheduling')}
                        </h3>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={scheduleEnabled}
                                onChange={(e) => setScheduleEnabled(e.target.checked)}
                                className="rounded border-gray-300 text-blue-500 focus:ring-blue-400"
                            />
                            <span className="text-sm text-gray-600">{t('manager.modules.claudeQuery.digests.enableAutoSend', {}, 'Aktiver automatisk utsendelse')}</span>
                        </label>
                    </div>

                    {scheduleEnabled && (
                        <div className="bg-blue-50/50 rounded-lg p-4 space-y-3">
                            <div className="flex items-center gap-4 flex-wrap">
                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.common.frequency', {}, 'Frekvens')}</label>
                                    <select
                                        value={scheduleFrequency}
                                        onChange={(e) => setScheduleFrequency(e.target.value)}
                                        className="px-3 py-1.5 border border-gray-200 rounded text-sm bg-white
                                                 focus:outline-none focus:border-blue-400"
                                    >
                                        <option value="daily">{t('manager.modules.claudeQuery.common.daily', {}, 'Daglig')}</option>
                                        <option value="weekly">{t('manager.modules.claudeQuery.common.weekly', {}, 'Ukentlig')}</option>
                                        <option value="monthly">{t('manager.modules.claudeQuery.common.monthly', {}, 'Månedlig')}</option>
                                    </select>
                                </div>

                                {scheduleFrequency === 'weekly' && (
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.common.day', {}, 'Dag')}</label>
                                        <select
                                            value={scheduleDayOfWeek}
                                            onChange={(e) => setScheduleDayOfWeek(parseInt(e.target.value))}
                                            className="px-3 py-1.5 border border-gray-200 rounded text-sm bg-white
                                                     focus:outline-none focus:border-blue-400"
                                        >
                                            <option value={1}>{t('manager.modules.claudeQuery.common.monday', {}, 'Mandag')}</option>
                                            <option value={2}>{t('manager.modules.claudeQuery.common.tuesday', {}, 'Tirsdag')}</option>
                                            <option value={3}>{t('manager.modules.claudeQuery.common.wednesday', {}, 'Onsdag')}</option>
                                            <option value={4}>{t('manager.modules.claudeQuery.common.thursday', {}, 'Torsdag')}</option>
                                            <option value={5}>{t('manager.modules.claudeQuery.common.friday', {}, 'Fredag')}</option>
                                            <option value={6}>{t('manager.modules.claudeQuery.common.saturday', {}, 'Lørdag')}</option>
                                            <option value={0}>{t('manager.modules.claudeQuery.common.sunday', {}, 'Søndag')}</option>
                                        </select>
                                    </div>
                                )}

                                {scheduleFrequency === 'monthly' && (
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.digests.dayOfMonth', {}, 'Dag i måneden')}</label>
                                        <input
                                            type="number"
                                            value={scheduleDayOfMonth}
                                            onChange={(e) => setScheduleDayOfMonth(parseInt(e.target.value) || 1)}
                                            min={1}
                                            max={28}
                                            className="w-16 px-3 py-1.5 border border-gray-200 rounded text-sm text-center
                                                     focus:outline-none focus:border-blue-400"
                                        />
                                    </div>
                                )}

                                <div>
                                    <label className="block text-xs text-gray-500 mb-1">{t('manager.modules.claudeQuery.common.time', {}, 'Klokkeslett')}</label>
                                    <div className="flex items-center gap-1">
                                        <input
                                            type="number"
                                            value={scheduleHour}
                                            onChange={(e) => setScheduleHour(parseInt(e.target.value) || 0)}
                                            min={0}
                                            max={23}
                                            className="w-14 px-2 py-1.5 border border-gray-200 rounded text-sm text-center
                                                     focus:outline-none focus:border-blue-400"
                                        />
                                        <span className="text-gray-400">:</span>
                                        <input
                                            type="number"
                                            value={scheduleMinute}
                                            onChange={(e) => setScheduleMinute(parseInt(e.target.value) || 0)}
                                            min={0}
                                            max={59}
                                            step={5}
                                            className="w-14 px-2 py-1.5 border border-gray-200 rounded text-sm text-center
                                                     focus:outline-none focus:border-blue-400"
                                        />
                                    </div>
                                </div>
                            </div>

                            <p className="text-xs text-blue-600 font-medium">
                                {getScheduleDescription()}
                            </p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};
